import { useMemo, useState, type FormEvent } from 'react'
import {
  useTicketTypes,
  useCreateTicketType,
  usePatchTicketType,
  useDeleteTicketType
} from '../hooks/useTicketTypes'
import {
  ModalShell, Field, inputClass,
  CancelButton, PrimaryButton, DangerButton, ServerError
} from '../components/modal-form'
import { Icon } from '../components/Icon'
import { useMe } from '../hooks/useMe'
import { formatGBP, parseGBP } from '../lib/money'
import { TICKET_TYPE_CATEGORIES } from '../types'
import type { TicketType, TicketTypeCreate } from '../types'

// AdminTicketTypes — admin-only page managing the STANDARD ticket-type
// catalogue (Feature 4). Event managers pick from this list on each event's
// Ticketing tab. Mirrors AdminHazardLibrary: grouped by category, click to
// edit, "+ New" opens the editor. Deletion is FK-protected server-side (a type
// in use returns 409 → we surface "deactivate instead").

export function AdminTicketTypes() {
  const { data: me } = useMe()
  const { data: types, isLoading, error } = useTicketTypes({ active: 'all' })
  const [editing, setEditing] = useState<TicketType | 'new' | null>(null)

  const grouped = useMemo(() => {
    const map = new Map<string, TicketType[]>()
    for (const t of types ?? []) {
      const arr = map.get(t.category) ?? []
      arr.push(t)
      map.set(t.category, arr)
    }
    // Order groups by the canonical category order.
    return (TICKET_TYPE_CATEGORIES as readonly string[])
      .filter((c) => map.has(c))
      .map((c) => [c, map.get(c)!] as [string, TicketType[]])
  }, [types])

  if (me && !me.isAdmin) {
    return (
      <div className="space-y-5">
        <div>
          <h1 className="text-3xl mb-1">Ticket types</h1>
          <p className="text-ink-soft">Admin only.</p>
        </div>
        <div className="bg-oxblood-soft border border-oxblood text-oxblood rounded p-4 text-sm">
          Editing the ticket-type catalogue requires admin permissions.
        </div>
      </div>
    )
  }

  return (
    <div className="space-y-5">
      <div className="flex items-end justify-between gap-4 flex-wrap">
        <div>
          <h1 className="text-3xl mb-1">Ticket types</h1>
          <p className="text-ink-soft max-w-2xl">
            The standard catalogue of ticket types. Event managers pick from this
            list on each event's Ticketing tab and set a per-event price. Keep the
            names consistent — they're what gets matched to MERAC ticket classes.
          </p>
        </div>
        <button
          type="button"
          onClick={() => setEditing('new')}
          className="px-4 py-2 text-sm font-semibold rounded bg-green text-parchment hover:bg-green/90 transition-colors"
        >
          + New ticket type
        </button>
      </div>

      {isLoading && <div className="text-ink-soft text-sm font-mono">Loading ticket types…</div>}

      {error && (
        <div className="bg-oxblood-soft border border-oxblood text-oxblood rounded p-4 text-sm">
          <div className="font-semibold mb-1">Could not load ticket types</div>
          <pre className="text-xs whitespace-pre-wrap">{(error as Error).message}</pre>
        </div>
      )}

      {types && types.length === 0 && (
        <div className="bg-white border border-line rounded p-10 text-center">
          <h3 className="font-display text-lg mb-1">No ticket types yet</h3>
          <p className="text-sm text-ink-soft mb-4 max-w-md mx-auto">
            Add ticket types here and event managers can pull them onto each event.
          </p>
          <button
            type="button"
            onClick={() => setEditing('new')}
            className="px-4 py-2 rounded text-sm font-semibold bg-green text-parchment hover:bg-green/90 transition-colors"
          >
            + Add the first ticket type
          </button>
        </div>
      )}

      {grouped.map(([category, entries]) => (
        <section key={category} className="bg-white border border-line rounded overflow-hidden">
          <header className="px-4 py-2.5 border-b border-line bg-parchment/60 flex items-baseline justify-between">
            <h2 className="text-sm font-semibold">{category}</h2>
            <span className="text-xs text-ink-soft font-mono">
              {entries.length} type{entries.length === 1 ? '' : 's'}
            </span>
          </header>
          <div className="divide-y divide-line">
            {entries.map((t) => (
              <button
                key={t.id}
                type="button"
                onClick={() => setEditing(t)}
                className="w-full text-left px-4 py-3 flex items-center gap-4 hover:bg-parchment/40 transition-colors"
              >
                <Icon name="ticket" size={16} className="text-green flex-shrink-0" />
                <div className="flex-1 min-w-0">
                  <div className="font-semibold text-sm">
                    {t.name}
                    {!t.isActive && (
                      <span className="ml-2 text-[11px] font-mono uppercase tracking-wide text-ink-soft border border-line rounded px-1.5 py-0.5">
                        inactive
                      </span>
                    )}
                  </div>
                </div>
                <span className="text-xs font-mono text-ink-soft whitespace-nowrap">
                  {t.defaultPricePence != null ? formatGBP(t.defaultPricePence) : 'no default'}
                </span>
                {t.isConcession && (
                  <span className="text-[11px] font-mono px-2 py-0.5 rounded" style={{ background: '#dcebe5', color: '#0a4a3a' }}>
                    concession
                  </span>
                )}
              </button>
            ))}
          </div>
        </section>
      ))}

      {editing && (
        <TicketTypeEditor
          initial={editing === 'new' ? null : editing}
          onClose={() => setEditing(null)}
        />
      )}
    </div>
  )
}

function TicketTypeEditor({ initial, onClose }: { initial: TicketType | null; onClose: () => void }) {
  const isEditing = initial != null
  const createMut = useCreateTicketType()
  const patchMut = usePatchTicketType()
  const deleteMut = useDeleteTicketType()

  const [name, setName] = useState(initial?.name ?? '')
  const [category, setCategory] = useState<string>(initial?.category ?? 'Adult')
  const [isConcession, setIsConcession] = useState(initial?.isConcession ?? false)
  const [isActive, setIsActive] = useState(initial?.isActive ?? true)
  const [price, setPrice] = useState(initial?.defaultPricePence == null ? '' : formatGBP(initial.defaultPricePence))

  const trimmed = name.trim()
  const nameError = trimmed.length === 0 ? 'Name is required' : trimmed.length > 120 ? 'Name must be 120 characters or fewer' : undefined
  const priceTrim = price.trim()
  const priceError = priceTrim !== '' && parseGBP(priceTrim) == null ? 'Enter a £ amount or leave blank' : undefined
  const isValid = !nameError && !priceError
  const saving = createMut.isPending || patchMut.isPending
  const deleting = deleteMut.isPending
  const serverError = (createMut.error ?? patchMut.error ?? deleteMut.error) as Error | null

  function handleSubmit(e: FormEvent) {
    e.preventDefault()
    if (!isValid || saving) return
    const body: TicketTypeCreate = {
      name: trimmed,
      category: category as TicketTypeCreate['category'],
      isConcession,
      defaultPricePence: priceTrim === '' ? null : parseGBP(priceTrim),
      isActive,
      sortOrder: initial?.sortOrder ?? 0
    }
    if (isEditing && initial) {
      patchMut.mutate({ id: initial.id, body }, { onSuccess: () => onClose() })
    } else {
      createMut.mutate(body, { onSuccess: () => onClose() })
    }
  }

  function handleDelete() {
    if (!initial) return
    const ok = window.confirm(`Remove "${initial.name}" from the catalogue? If it's in use by an event this will fail — deactivate it instead.`)
    if (!ok) return
    deleteMut.mutate(initial.id, { onSuccess: () => onClose() })
  }

  return (
    <ModalShell
      title={isEditing ? 'Edit ticket type' : 'New ticket type'}
      onClose={onClose}
      onSubmit={handleSubmit}
      size="lg"
      footer={
        <>
          {isEditing && (
            <DangerButton onClick={handleDelete} disabled={saving || deleting}>
              {deleting ? 'Removing…' : 'Remove'}
            </DangerButton>
          )}
          <CancelButton onClick={onClose} disabled={saving} />
          <PrimaryButton disabled={!isValid || saving}>
            {saving ? 'Saving…' : isEditing ? 'Save changes' : 'Add ticket type'}
          </PrimaryButton>
        </>
      }
      body={
        <div className="space-y-4">
          <Field label="Name" error={nameError}>
            <input
              type="text"
              value={name}
              onChange={(e) => setName(e.target.value)}
              maxLength={120}
              autoFocus
              required
              className={inputClass(!!nameError)}
              placeholder="e.g. Adult, Member Child, Group Coordinator"
            />
          </Field>

          <div className="grid grid-cols-2 gap-3">
            <Field label="Category" hint="Groups the catalogue and the per-event picker.">
              <select value={category} onChange={(e) => setCategory(e.target.value)} className={inputClass(false)}>
                {TICKET_TYPE_CATEGORIES.map((c) => (
                  <option key={c} value={c}>{c}</option>
                ))}
              </select>
            </Field>
            <Field label="Standard price (optional)" error={priceError} hint="Pre-fills the price when added to an event.">
              <input
                type="text"
                value={price}
                onChange={(e) => setPrice(e.target.value)}
                className={inputClass(!!priceError)}
                placeholder="£0.00"
              />
            </Field>
          </div>

          <div className="flex items-center gap-6">
            <label className="flex items-center gap-2 text-sm">
              <input type="checkbox" checked={isConcession} onChange={(e) => setIsConcession(e.target.checked)} />
              Concession (child / carer / member etc.)
            </label>
            <label className="flex items-center gap-2 text-sm">
              <input type="checkbox" checked={isActive} onChange={(e) => setIsActive(e.target.checked)} />
              Active
            </label>
          </div>

          {serverError && <ServerError title="Could not save" error={serverError} />}
        </div>
      }
    />
  )
}
